import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown, Check, ArrowRight, ArrowLeft, ShieldCheck, Globe, Building2, Mail, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * Enterprise Tenant Onboarding Wizard — 4-step guided flow.
 *
 *   1. DNS verification (custom domain)
 *   2. Logo + primary color upload
 *   3. Child teams under the tenant's parent_organization_id
 *   4. Success Engineer introduction
 *
 * Every step calls existing endpoints — no new backend surface needed
 * except for the child-team creation which we defer to Success Engineer
 * outreach (that keeps the wizard scoped and prevents unauthorized
 * tenant creation).
 */
const STEPS = ["dns", "branding", "teams", "handoff"];

export default function EnterpriseOnboarding() {
  const navigate = useNavigate();
  const [tier, setTier] = useState(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/tier");
        setTier(data);
        if (data.tier !== "enterprise") {
          toast.info("Enterprise onboarding is only available on the Enterprise tier.");
        }
      } catch { /* silent */ }
    })();
  }, []);

  if (!tier) {
    return (
      <div className="flex items-center gap-2 text-white/55 text-sm p-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
    );
  }
  if (tier.tier !== "enterprise") {
    return (
      <div className="max-w-2xl mx-auto p-8 space-y-4 text-center" data-testid="onboarding-locked">
        <Crown className="h-10 w-10 mx-auto" style={{ color: "#FFD700" }} />
        <h1 className="font-display font-black text-3xl text-white">Enterprise-only workflow</h1>
        <p className="text-white/60">This 4-step onboarding wizard is available to Enterprise tier tenants only. Book a consultation to unlock.</p>
        <Link to="/enterprise/contact"><Button className="text-[var(--ink-0)] font-bold rounded-full mt-4" style={{ backgroundColor: "#FFD700" }}>Book Enterprise consultation</Button></Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl" data-testid="enterprise-onboarding-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "#FFD700" }}>
          <Crown className="h-3.5 w-3.5" /> Enterprise Onboarding · 4 steps
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-1">
          Get your tenant production-ready in 4 steps.
        </h1>
      </div>

      <StepNav step={step} onStep={setStep} />

      {step === 0 && <StepDNS onNext={() => setStep(1)} />}
      {step === 1 && <StepBranding onBack={() => setStep(0)} onNext={() => setStep(2)} />}
      {step === 2 && <StepTeams onBack={() => setStep(1)} onNext={() => setStep(3)} />}
      {step === 3 && <StepHandoff onBack={() => setStep(2)} onFinish={() => navigate("/dashboard/settings")} />}
    </div>
  );
}

function StepNav({ step, onStep }) {
  const labels = [
    { key: "dns",     label: "1 · DNS verify",   Icon: Globe },
    { key: "branding",label: "2 · Branding",     Icon: ShieldCheck },
    { key: "teams",   label: "3 · Child teams",  Icon: Building2 },
    { key: "handoff", label: "4 · Handoff",      Icon: Mail },
  ];
  return (
    <div className="rounded-xl ink-card p-2 flex flex-wrap gap-1" data-testid="onboarding-stepnav">
      {labels.map((l, i) => {
        const Icon = l.Icon;
        const active = i === step;
        const done = i < step;
        return (
          <button
            key={l.key}
            onClick={() => onStep(i)}
            data-testid={`onboarding-step-${l.key}`}
            className={`flex-1 min-w-[150px] inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-xs font-bold transition ${active ? "text-[var(--ink-0)]" : done ? "text-white/85 bg-white/5" : "text-white/45 hover:text-white/80"}`}
            style={active ? { backgroundColor: "#FFD700" } : {}}
          >
            {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

function StepDNS({ onNext }) {
  const [state, setState] = useState(null);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const { data } = await api.get("/teams/me/branding");
    setState(data);
    setDomain(data?.branding?.custom_domain || "");
  };
  useEffect(() => { load(); }, []);

  const savedomain = async () => {
    setBusy(true);
    try {
      await api.patch("/teams/me/branding", { custom_domain: domain });
      await load();
      toast.success("Domain saved — publish the TXT record next");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/teams/me/branding/verify");
      toast.success("Domain verified");
      await load();
      onNext();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const b = state?.branding || {};
  const verified = !!b.dns_verified;
  const qname = b.custom_domain ? `_armiq.${b.custom_domain}` : "—";

  return (
    <section className="rounded-2xl ink-card p-6 md:p-7 space-y-4" data-testid="onboarding-step-dns">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45">Step 1</div>
        <h2 className="font-display font-black text-2xl text-white mt-1">Point your custom domain at ArmIQ.</h2>
        <p className="text-sm text-white/60 mt-1">Athletes will visit your tenant on this domain. We&apos;ll issue a DNS TXT token that proves you own it.</p>
      </div>
      <div>
        <Label className="text-white/70">Custom domain</Label>
        <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="performance.your-org.com" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="onboarding-dns-domain" />
      </div>
      {b.dns_token && (
        <div className="rounded-lg p-3 text-xs border" style={{ backgroundColor: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.4)" }}>
          <div className="text-white/70">Publish TXT record:</div>
          <div className="mt-1 text-white font-mono-num flex items-center gap-2">
            <code className="bg-[var(--ink-1)] px-1.5 py-0.5 rounded">{qname}</code>
            <span className="text-white/45">=</span>
            <code className="bg-[var(--ink-1)] px-1.5 py-0.5 rounded">{b.dns_token}</code>
            <button onClick={async () => { await navigator.clipboard.writeText(b.dns_token); setCopied(true); setTimeout(() => setCopied(false), 1200); }} className="text-white/60 hover:text-white">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button onClick={savedomain} disabled={!domain || busy} variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10">Save domain</Button>
        <Button onClick={verified ? onNext : verify} disabled={busy} className="text-[var(--ink-0)] font-bold" style={{ backgroundColor: "#FFD700" }} data-testid="onboarding-dns-next">
          {verified ? <>Continue <ArrowRight className="h-4 w-4 ml-1" /></> : busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify DNS"}
        </Button>
      </div>
    </section>
  );
}

function StepBranding({ onBack, onNext }) {
  const [form, setForm] = useState({ display_name: "", primary_color: "", logo_url: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/teams/me/branding").then(({ data }) => {
      const b = data.branding || {};
      setForm({ display_name: b.display_name || "", primary_color: b.primary_color || "", logo_url: b.logo_url || "" });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/teams/me/branding", Object.fromEntries(Object.entries(form).filter(([, v]) => !!v)));
      toast.success("Branding saved");
      onNext();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl ink-card p-6 md:p-7 space-y-4" data-testid="onboarding-step-branding">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45">Step 2</div>
        <h2 className="font-display font-black text-2xl text-white mt-1">Publish your brand.</h2>
        <p className="text-sm text-white/60 mt-1">Logo, primary color, and the display name your athletes see on every page.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-white/70">Display name</Label>
          <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="PitchLab Performance" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="onboarding-branding-name" />
        </div>
        <div>
          <Label className="text-white/70">Primary color (hex)</Label>
          <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} placeholder="#D90429" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white font-mono-num" data-testid="onboarding-branding-color" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-white/70">Logo URL</Label>
          <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://cdn.your-org.com/logo.svg" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="onboarding-branding-logo" />
        </div>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white bg-white/5 hover:bg-white/10"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button onClick={save} disabled={busy} className="text-[var(--ink-0)] font-bold" style={{ backgroundColor: "#FFD700" }} data-testid="onboarding-branding-next">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save + Continue <ArrowRight className="h-4 w-4 ml-1" /></>}
        </Button>
      </div>
    </section>
  );
}

function StepTeams({ onBack, onNext }) {
  const [rollup, setRollup] = useState(null);
  useEffect(() => { api.get("/organization/rollup").then(({ data }) => setRollup(data)).catch(() => {}); }, []);

  return (
    <section className="rounded-2xl ink-card p-6 md:p-7 space-y-4" data-testid="onboarding-step-teams">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45">Step 3</div>
        <h2 className="font-display font-black text-2xl text-white mt-1">Your organization&apos;s teams.</h2>
        <p className="text-sm text-white/60 mt-1">Every team already sharing your <code className="text-white">parent_organization_id</code> is listed here. To add an affiliate, your Success Engineer handles the linkage.</p>
      </div>
      <div className="rounded-lg border border-[var(--ink-border)] divide-y divide-white/5">
        {(rollup?.teams || []).map((t) => (
          <div key={t.team_id} className="p-3 flex items-center justify-between text-sm" data-testid={`onboarding-team-${t.team_id}`}>
            <div>
              <div className="text-white font-semibold">{t.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{t.tier} tier · {t.totals?.total ?? 0} athletes</div>
            </div>
          </div>
        ))}
        {(!rollup || rollup.teams.length === 0) && (
          <div className="p-6 text-center text-sm text-white/45">No child teams yet — Success Engineer will provision them in Step 4.</div>
        )}
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white bg-white/5 hover:bg-white/10"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button onClick={onNext} className="text-[var(--ink-0)] font-bold" style={{ backgroundColor: "#FFD700" }} data-testid="onboarding-teams-next">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
      </div>
    </section>
  );
}

function StepHandoff({ onBack, onFinish }) {
  return (
    <section className="rounded-2xl ink-card p-6 md:p-7 space-y-4" data-testid="onboarding-step-handoff">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45">Step 4</div>
        <h2 className="font-display font-black text-2xl text-white mt-1">Meet your Success Engineer.</h2>
        <p className="text-sm text-white/60 mt-1">Reach out to configure custom AI models, executive dashboards, affiliate provisioning, or SSO. Your Success Engineer replies within one business day.</p>
      </div>
      <div className="rounded-lg p-4 border" style={{ backgroundColor: "rgba(255,215,0,0.06)", borderColor: "rgba(255,215,0,0.4)" }}>
        <ul className="text-xs text-white/75 space-y-1">
          <li>• Provision additional child teams under your parent_organization_id</li>
          <li>• Configure executive KPIs + affiliate benchmarking cards</li>
          <li>• Wire enterprise data pipelines (API keys, webhooks, SFTP feeds)</li>
          <li>• Custom AI models trained on your organization&apos;s labeled outcomes</li>
        </ul>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white bg-white/5 hover:bg-white/10"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <div className="flex gap-2">
          <Link to="/enterprise/contact">
            <Button variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10"><Mail className="h-4 w-4 mr-2" /> Email Success Engineer</Button>
          </Link>
          <Button onClick={onFinish} className="text-[var(--ink-0)] font-bold" style={{ backgroundColor: "#FFD700" }} data-testid="onboarding-finish">Finish <Check className="h-4 w-4 ml-1" /></Button>
        </div>
      </div>
    </section>
  );
}
